import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import config from '../config';

const SocketContext = createContext(null);

export const SocketProvider = ({ children, authUser, selectedProject }) => {
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [pendingMessages, setPendingMessages] = useState([]);
  const socketRef = useRef(null);

  // Socket bağlantısını kur
  useEffect(() => {
    if (!authUser) {
      // Kullanıcı yoksa socket'i kapat
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Zaten bağlı socket varsa yeniden bağlanma
    if (socketRef.current?.connected) {
      return;
    }

    console.log('Socket.io connecting...', config.appUrl);
    
    const newSocket = io(config.appUrl, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = newSocket;

    newSocket.on('connect', () => {
      console.log('Socket.io connected:', newSocket.id);
      setIsConnected(true);
      
      // Kullanıcıyı online olarak bildir
      const userData = {
        userId: authUser.id,
        userName: `${authUser.name || ''} ${authUser.surname || ''}`.trim(),
        userRoles: authUser.userRoles || [],
        projectId: selectedProject?.id || null,
        profileImageUrl: authUser.profileImageUrl
      };
      
      console.log('Emitting user:online', userData);
      newSocket.emit('user:online', userData);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket.io disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket.io connection error:', error);
      setIsConnected(false);
    });

    // Online kullanıcı listesi
    newSocket.on('users:online', (users) => {
      console.log('Online users updated:', users.length);
      setOnlineUsers(users);
    });

    // Bekleyen mesajlar
    newSocket.on('messages:pending', (messages) => {
      console.log('Pending messages received:', messages.length);
      setPendingMessages(messages);
    });

    setSocket(newSocket);

    // Cleanup
    return () => {
      console.log('Socket.io cleanup');
      if (newSocket) {
        newSocket.emit('user:offline', { userId: authUser.id });
        newSocket.disconnect();
      }
    };
  }, [authUser?.id]);

  // Proje değiştiğinde güncelle
  useEffect(() => {
    if (socket && isConnected && authUser && selectedProject) {
      socket.emit('user:online', {
        userId: authUser.id,
        userName: `${authUser.name || ''} ${authUser.surname || ''}`.trim(),
        userRoles: authUser.userRoles || [],
        projectId: selectedProject.id,
        profileImageUrl: authUser.profileImageUrl
      });
    }
  }, [selectedProject?.id, socket, isConnected, authUser]);

  // Destek personeli ara
  const findSupport = useCallback((projectId) => {
    if (socket && isConnected) {
      return new Promise((resolve) => {
        socket.emit('chat:find-support', {
          projectId,
          senderUserId: authUser?.id
        });
        
        socket.once('chat:support-found', (data) => {
          resolve(data);
        });
        
        // Timeout
        setTimeout(() => {
          resolve({ available: false, supporters: [], message: 'Bağlantı zaman aşımına uğradı.' });
        }, 5000);
      });
    }
    return Promise.resolve({ available: false, supporters: [], message: 'Bağlantı yok.' });
  }, [socket, isConnected, authUser]);

  // Mesaj gönder
  const sendMessage = useCallback((message, receiverId) => {
    if (socket && isConnected) {
      socket.emit('chat:message', { message, receiverId });
    }
  }, [socket, isConnected]);

  // Yazıyor bildirimi
  const sendTyping = useCallback((receiverId, isTyping) => {
    if (socket && isConnected && authUser) {
      socket.emit('chat:typing', {
        senderId: authUser.id,
        senderName: `${authUser.name || ''} ${authUser.surname || ''}`.trim(),
        receiverId,
        isTyping
      });
    }
  }, [socket, isConnected, authUser]);

  // Kullanıcının online olup olmadığını kontrol et
  const isUserOnline = useCallback((userId) => {
    return onlineUsers.some(u => u.id === userId);
  }, [onlineUsers]);

  // Belirli rollere sahip online kullanıcıları getir
  const getOnlineUsersByRole = useCallback((roleIds) => {
    return onlineUsers.filter(user => 
      user.userRoles?.some(role => roleIds.includes(role.id))
    );
  }, [onlineUsers]);

  const value = {
    socket,
    onlineUsers,
    isConnected,
    pendingMessages,
    findSupport,
    sendMessage,
    sendTyping,
    isUserOnline,
    getOnlineUsersByRole
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    // Context dışında kullanılırsa boş değerler döndür
    return {
      socket: null,
      onlineUsers: [],
      isConnected: false,
      pendingMessages: [],
      findSupport: () => Promise.resolve({ available: false, supporters: [] }),
      sendMessage: () => {},
      sendTyping: () => {},
      isUserOnline: () => false,
      getOnlineUsersByRole: () => []
    };
  }
  return context;
};

export default SocketContext;


